import '@/components/index.js';
import { bindRegistrationFirebaseBridge, waitForRegistrationDependencies } from './core/FirebaseBridge.js';
import { logRegistrationError } from './core/errors.js';
import { pushToLegacyGlobals } from './core/RegistrationContext.js';
import { bindDelegatedEvents } from './ui/DelegatedEvents.js';
import { installRegistrationModuleGlobals } from './modules/installGlobals.js';
import { loadLegacyRegistrationApp, getLegacyBootFn } from './legacy/loadLegacyApp.js';
import { installVariantRouterGlobal } from './core/VariantRouter.js';
import { resolveDisplayRoute } from './display/urlParse.js';

let bootstrapped = false;

function showRegistrationBootRetryUi(): void {
    const main = document.getElementById('mainContent');
    if (!main) return;
    if (main.querySelector('[data-vivlico-boot-retry="1"]')) return;
    const banner = document.createElement('div');
    banner.setAttribute('data-vivlico-boot-retry', '1');
    banner.className = 'registrationBootRetry';
    banner.style.cssText = 'margin:16px auto;max-width:420px;padding:14px 16px;text-align:center;font:500 14px/1.4 DM Sans,sans-serif;color:#1a1a1a;';
    banner.innerHTML = '<p style="margin:0 0 10px;">Die Anmeldung lädt gerade nicht vollständig.</p>'
        + '<button type="button" data-vivlico-boot-retry-btn="1" '
        + 'style="appearance:none;border:0;border-radius:8px;padding:10px 16px;background:#1a1a1a;color:#fff;font:600 14px DM Sans,sans-serif;cursor:pointer;">'
        + 'Erneut laden</button>';
    main.appendChild(banner);
    const btn = banner.querySelector('[data-vivlico-boot-retry-btn="1"]');
    if (btn) {
        btn.addEventListener('click', () => {
            window.location.reload();
        });
    }
}

function seedUrlArrayFromDisplayRoute(): void {
    const g = globalThis as { urlArray?: string[] };
    if (Array.isArray(g.urlArray) && g.urlArray.length > 0) return;
    try {
        const route = resolveDisplayRoute();
        if (route?.urlArray?.length) {
            g.urlArray = route.urlArray.slice();
        }
    } catch {
        /* ignore */
    }
}

async function bootRegistrationApp(): Promise<void> {
    if (bootstrapped) return;
    bootstrapped = true;

    installVariantRouterGlobal();
    bindDelegatedEvents();
    const { registerAction } = await import('./ui/DelegatedEvents.js');
    registerAction('toast:close', () => {
        const fn = (globalThis as { closeRegistrationToast?: () => void }).closeRegistrationToast;
        if (typeof fn === 'function') fn();
    });
    pushToLegacyGlobals();
    seedUrlArrayFromDisplayRoute();

    let ready = await waitForRegistrationDependencies(8000);
    if (!ready) {
        logRegistrationError('boot', new Error('dependencies not ready, retrying'));
        ready = await waitForRegistrationDependencies(5000);
    }
    if (!ready) {
        bootstrapped = false;
        logRegistrationError('boot', new Error('dependencies not ready'));
        showRegistrationBootRetryUi();
        return;
    }

    bindRegistrationFirebaseBridge();

    try {
        // Core sections first; module globals must be ready before legacy onLoad.
        await loadLegacyRegistrationApp();
        try {
            await installRegistrationModuleGlobals();
        } catch (e) {
            logRegistrationError('installModules', e);
        }
    } catch (e) {
        bootstrapped = false;
        logRegistrationError('loadLegacy', e);
        return;
    }

    const boot = getLegacyBootFn();
    if (!boot) {
        bootstrapped = false;
        logRegistrationError('boot', new Error('bootRegistrationApp missing after legacy load'));
        return;
    }

    const g = globalThis as { __vivMonolithBooted?: boolean; __vivStopDisplayHeightSync?: () => void };
    g.__vivMonolithBooted = true;
    if (typeof g.__vivStopDisplayHeightSync === 'function') {
        g.__vivStopDisplayHeightSync();
    }

    try {
        await boot();
    } catch (error) {
        bootstrapped = false;
        logRegistrationError('bootRegistrationApp', error);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { void bootRegistrationApp(); }, { once: true });
} else {
    void bootRegistrationApp();
}

export { bootRegistrationApp };
